import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import InstallAppButton from "../components/InstallAppButton";
import { UserCircle, LogOut, Save, Camera, Send, FileText, Info, CheckCircle2, Clock, Users, Download, ExternalLink, FolderOpen, Signature, Upload, Trash2, CalendarClock, CalendarCheck, X, Bell, BellOff, Sparkles } from "lucide-react";
import { isPushSupported, currentPushPermission, subscribeToPush, unsubscribeFromPush, ensureServiceWorker } from "../lib/push";

// Photo guide (generic — user will customize per treatment later)
const PHOTO_GUIDE = [
  { tag: "frontal", label: "Frontale", desc: "Guarda dritto, viso rilassato, senza sorriso." },
  { tag: "lateral_l", label: "Profilo sinistro", desc: "90° a sinistra, guarda dritto in avanti." },
  { tag: "lateral_r", label: "Profilo destro", desc: "90° a destra, guarda dritto in avanti." },
  { tag: "detail", label: "Dettaglio zona", desc: "Ravvicinato della zona da valutare." },
];

const TIPS = [
  "Luce naturale (di giorno, vicino a una finestra).",
  "Senza filtri, senza trucco.",
  "Sfondo neutro (parete bianca o beige).",
  "Distanza ~50 cm, cellulare all'altezza degli occhi.",
  "Capelli raccolti se le foto sono del viso.",
];

const TREATMENTS = [
  "Consulto generale", "Botulino", "Filler labbra", "Filler zigomi", "Filler nasolabiale",
  "Blefaroplastica", "Rinoplastica", "Otoplastica", "Lifting", "Mastoplastica additiva",
  "Mastoplastica riduttiva", "Lipofilling", "Liposcultura", "Addominoplastica", "Ginecomastia",
  "Peeling chimico", "Skinbooster", "Fili di trazione", "Trapianto di capelli", "Altro",
];

const _ES_MAP = {"Professionista cambiato": "Profesional cambiado", "Nessun professionista collegato": "Ningún profesional vinculado", "Per prenotare un appuntamento, cerca prima un professionista della salute e collegalo al tuo profilo.": "Para reservar una cita, primero busca un profesional de la salud y vincúlalo a tu perfil.", "Cerca un professionista →": "Buscar un profesional →", "I tuoi professionisti": "Tus profesionales", "Il tuo professionista": "Tu profesional", "I miei dati": "Mis datos", "Prenota": "Reservar", "Documenti": "Documentos", "Consensi": "Consentimientos", "Le mie foto": "Mis fotos", "Storico": "Historial", "Prenota un appuntamento": "Reserva una cita", "Con quale professionista": "Con qué profesional", "Non hai ancora professionisti collegati.": "Aún no tienes profesionales vinculados.", "Selezionato": "Seleccionado", "Seleziona": "Seleccionar", "Che tipo di visita?": "¿Qué tipo de visita?", "Consulto": "Consulta", "Prima visita per valutare il tuo caso": "Primera visita para valorar tu caso", "Trattamento": "Tratamiento", "Prenota un servizio specifico": "Reserva un servicio específico", "Scegli il giorno": "Elige el día", "disponibile": "disponible", "quasi pieno": "casi lleno", "chiuso": "cerrado", "Per quale trattamento vorresti consultare?": "¿Para qué tratamiento te gustaría consultar?", "Consulta, controllo...": "Consulta, control…", "Data selezionata": "Fecha seleccionada", "Orari disponibili": "Horarios disponibles", "Cercando disponibilità…": "Buscando disponibilidad…", "Nessun orario disponibile in questa data.": "No hay horarios disponibles en esta fecha.", "Prova un altro giorno o cambia professionista.": "Prueba otro día o cambia de profesional.", "Messaggio per il medico (opzionale)": "Mensaje para el médico (opcional)", "Racconta cosa vorresti trattare, da quando, aspettative...": "Cuenta qué te gustaría tratar, desde cuándo, expectativas…", "Confermando...": "Confirmando…", "Seleziona un orario": "Selecciona un horario", "I tuoi appuntamenti": "Tus citas", "Ancora nessun appuntamento.": "Aún no tienes citas.", "Riprogramma": "Reprogramar", "Annulla": "Cancelar", "Annullare questo appuntamento?": "¿Cancelar esta cita?", "Appuntamento annullato": "Cita cancelada", "Appuntamento confermato ✓": "Cita confirmada ✓", "Seleziona un professionista": "Selecciona un profesional", "Seleziona data e orario": "Selecciona fecha y horario", "Riprogramma appuntamento": "Reprogramar cita", "Nuova data": "Nueva fecha", "Nuovo orario": "Nuevo horario", "Salvando...": "Guardando…", "Conferma nuovo orario": "Confirmar nuevo horario", "I tuoi documenti clinici": "Tus documentos clínicos", "Analisi, referti, esami istologici e documenti condivisi con il tuo professionista.": "Análisis, informes, estudios histológicos y documentos compartidos con tu profesional.", "Invia al professionista": "Enviar al profesional", "Destinatario": "Destinatario", "Tipo": "Tipo", "Analisi": "Análisis", "Istologia": "Histología", "Ecografia": "Ecografía", "Referto": "Informe", "Pre-operatorio": "Preoperatorio", "Altro": "Otro", "Note (opzionale)": "Notas (opcional)", "Es. Analisi del sangue del 12/07...": "Ej. Análisis de sangre del 12/07…", "Caricamento in corso…": "Subiendo…", "Trascina qui il file o fai clic per selezionare": "Arrastra el archivo o haz clic para seleccionar", "PDF o immagini · fino a 20 MB · più file consentiti": "PDF o imágenes · hasta 20 MB · varios archivos permitidos", "Solo il professionista selezionato potrà vedere questo documento.": "Solo el profesional seleccionado podrá ver este documento.", "Non ci sono ancora documenti. Carica il primo qui sopra oppure aspetta che il tuo medico condivida qualcosa con te.": "Aún no hay documentos. Sube el primero arriba o espera a que tu médico comparta algo contigo.", "Caricato da te": "Subido por ti", "altro": "otro", "Apri": "Abrir", "Scarica": "Descargar", "Eliminare questo documento?": "¿Eliminar este documento?", "Documento eliminato": "Documento eliminado", "Selecciona primero un profesional": "Selecciona primero un profesional", "I consensi firmati": "Los consentimientos firmados", "Consensi informati che hai firmato digitalmente.": "Consentimientos informados que has firmado digitalmente.", "Non hai ancora firmato consensi.": "Aún no has firmado consentimientos.", "Consenso informato": "Consentimiento informado", "Firmato il ": "Firmado el ", "Firmato": "Firmado", "Nome e cognome": "Nombre y apellidos", "Telefono": "Teléfono", "Data di nascita": "Fecha de nacimiento", "Codice fiscale": "Código fiscal", "Allergie": "Alergias", "Es. lattice, penicillina…": "Ej. látex, penicilina…", "Patologie": "Patologías", "Es. ipertensione, diabete…": "Ej. hipertensión, diabetes…", "Farmaci in uso": "Medicamentos actuales", "Elenca i farmaci che assumi": "Enumera los medicamentos que tomas", "Note aggiuntive": "Notas adicionales", "Qualsiasi informazione rilevante": "Cualquier información relevante", "Salvataggio…": "Guardando…", "Salva dati": "Guardar datos", "Nuova richiesta": "Nueva solicitud", "Cosa vorresti prenotare?": "¿Qué te gustaría reservar?", "Prima visita per valutare la tua richiesta.": "Primera visita para valorar tu solicitud.", "Prenota un trattamento specifico.": "Reserva un tratamiento específico.", "Trattamento di interesse": "Tratamiento de interés", "-- Seleziona --": "-- Selecciona --", "Messaggio per il medico": "Mensaje para el médico", "Racconta cosa vorresti trattare, da quando, aspettative…": "Cuenta qué te gustaría tratar, desde cuándo, expectativas…", "Invia richiesta e passa alle foto": "Enviar solicitud y pasar a las fotos", "Come farti le foto": "Cómo hacerte las fotos", "Seguendo queste indicazioni il medico potrà valutare al meglio il tuo caso.": "Siguiendo estas indicaciones el médico podrá valorar mejor tu caso.", "Non hai una richiesta aperta. Le foto verranno associate a una nuova consulta.": "No tienes una solicitud abierta. Las fotos se asociarán a una nueva consulta.", "Caricamento…": "Subiendo…", "Tocca per caricare": "Toca para subir", "Frontale": "Frontal", "Guarda dritto, viso rilassato, senza sorriso.": "Mira al frente, cara relajada, sin sonreír.", "Profilo sinistro": "Perfil izquierdo", "90° a sinistra, guarda dritto in avanti.": "90° a la izquierda, mira al frente.", "Profilo destro": "Perfil derecho", "90° a destra, guarda dritto in avanti.": "90° a la derecha, mira al frente.", "Dettaglio zona": "Detalle zona", "Ravvicinato della zona da valutare.": "Primer plano de la zona a valorar.", "Luce naturale (di giorno, vicino a una finestra).": "Luz natural (de día, cerca de una ventana).", "Senza filtri, senza trucco.": "Sin filtros, sin maquillaje.", "Sfondo neutro (parete bianca o beige).": "Fondo neutro (pared blanca o beige).", "Distanza ~50 cm, cellulare all'altezza degli occhi.": "Distancia ~50 cm, móvil a la altura de los ojos.", "Capelli raccolti se le foto sono del viso.": "Cabello recogido si las fotos son del rostro.", "Le tue richieste": "Tus solicitudes", "Ancora nessuna richiesta.": "Aún no hay solicitudes.", "foto": "foto(s)", "Aggiungi altra": "Añadir otra", "Aggiungi altre": "Añadir otras", "Notifiche disattivate": "Notificaciones desactivadas", "Notifiche attivate": "Notificaciones activadas", "Notifiche bloccate nelle impostazioni del browser": "Notificaciones bloqueadas en la configuración del navegador", "Disattiva notifiche": "Desactivar notificaciones", "Attiva notifiche push sul telefono": "Activar notificaciones push en el teléfono", "Messaggi": "Mensajes", "Conversazione con": "Conversación con", "il tuo medico": "tu médico", "Ancora nessun messaggio. Scrivi il primo qui sotto.": "Aún no hay mensajes. Escribe el primero aquí abajo.", "Scrivi un messaggio…": "Escribe un mensaje…", "Invia": "Enviar", "Invio…": "Enviando…", "Seleziona prima un professionista": "Selecciona primero un profesional", "Consenso allegato a questo trattamento": "Consentimiento asociado a este tratamiento", "Scarica e leggi il consenso prima di prenotare": "Descarga y lee el consentimiento antes de reservar", "Apri consenso": "Abrir consentimiento"};
const _tr = (s, lang) => (lang === "es" && _ES_MAP[s]) ? _ES_MAP[s] : s;

export default function PatientPortal() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const _lang = (i18n.language || "it").slice(0, 2);
  const tr = (s) => _tr(s, _lang);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState(() => {
    // Support ?tab=messages coming from a push-notification click.
    try {
      const p = new URLSearchParams(window.location.search).get("tab");
      if (p) return p;
    } catch { /* noop */ }
    return "data";
  });
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [profile, setProfile] = useState({ full_name: "", phone: "", date_of_birth: "", codice_fiscale: "", allergies: "", diseases: "", medications: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [reqForm, setReqForm] = useState({ type: "consulta", treatment: "", message: "" });
  const [creatingReq, setCreatingReq] = useState(false);
  const [uploadingTag, setUploadingTag] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [consents, setConsents] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [docCategory, setDocCategory] = useState("analisi");
  const [docNotes, setDocNotes] = useState("");
  const [docTargetDoctor, setDocTargetDoctor] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Booking state
  const todayIso = new Date().toISOString().slice(0, 10);
  const [bookDate, setBookDate] = useState(todayIso);
  const [bookTargetDoctor, setBookTargetDoctor] = useState("");
  const [bookMode, setBookMode] = useState("trattamento"); // "consulto" or "trattamento"
  const [bookTreatment, setBookTreatment] = useState("Consulta");
  const [bookTreatments, setBookTreatments] = useState([]);   // doctor's menu
  const [bookNotes, setBookNotes] = useState("");
  const [bookSlots, setBookSlots] = useState([]);
  const [bookSlotsLoading, setBookSlotsLoading] = useState(false);
  const [bookConfirming, setBookConfirming] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("patient_token");
    if (!token) { navigate("/portal"); return; }
    const h = { Authorization: `Bearer ${token}` };
    api.get("/patient-auth/me", { headers: h })
      .then((r) => {
        setMe(r.data.patient);
        setProfile({
          full_name: r.data.patient.full_name || "", phone: r.data.patient.phone || "",
          date_of_birth: r.data.patient.date_of_birth || "", codice_fiscale: r.data.patient.codice_fiscale || "",
          allergies: r.data.patient.allergies || "", diseases: r.data.patient.diseases || "",
          medications: r.data.patient.medications || "", notes: r.data.patient.notes || "",
        });
      })
      .catch(() => { localStorage.removeItem("patient_token"); navigate("/portal"); });
    api.get("/patient-portal/requests", { headers: h })
      .then((r) => setRequests(r.data || []))
      .catch(() => {});
    api.get("/patient-portal/doctors", { headers: h })
      .then((r) => setDoctors(r.data || []))
      .catch(() => {});
    api.get("/patient-portal/documents", { headers: h })
      .then((r) => setDocuments(r.data || []))
      .catch(() => {});
    api.get("/patient-portal/consents", { headers: h })
      .then((r) => setConsents(r.data || []))
      .catch(() => {});
    api.get("/patient-portal/appointments", { headers: h })
      .then((r) => setAppointments(r.data || []))
      .catch(() => {});
  }, [navigate]);

  // Refresh available slots whenever the picked date or target doctor changes on the "Prenota" tab.
  useEffect(() => {
    if (!me) return;
    loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookDate, bookTargetDoctor, me?.doctor_id, bookTreatment]);

  // Load month availability whenever the month or doctor or treatment changes
  const [monthDays, setMonthDays] = useState([]);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  useEffect(() => {
    if (!me) return;
    const target = bookTargetDoctor || me?.doctor_id;
    if (!target) return;
    const dur = getTreatmentDuration(bookTreatment);
    api.get(`/patient-portal/month-availability?year=${viewMonth.year}&month=${viewMonth.month}&doctor_id=${target}${dur ? `&duration_min=${dur}` : ""}`, { headers })
      .then((r) => setMonthDays(r.data?.days || []))
      .catch(() => setMonthDays([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMonth, bookTargetDoctor, me?.doctor_id, bookTreatment]);

  function getTreatmentDuration(name) {
    for (const t of bookTreatments) {
      if (t && typeof t === "object" && (t.name || "").toLowerCase() === (name || "").toLowerCase()) return t.duration_min;
    }
    return null;
  }

  // Load the target doctor's treatment menu whenever the target doctor changes.
  useEffect(() => {
    const target = bookTargetDoctor || me?.doctor_id;
    if (!target) { setBookTreatments([]); return; }
    api.get(`/public/doctor/${target}/treatments`).then((r) => {
      const list = r.data?.treatments || [];
      setBookTreatments(list);
      // pick first as default if current selection isn't in the list
      if (list.length && !list.includes(bookTreatment)) setBookTreatment(typeof list[0] === "string" ? list[0] : list[0].name);
    }).catch(() => setBookTreatments([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookTargetDoctor, me?.doctor_id]);

  const token = localStorage.getItem("patient_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Consent template auto-lookup: when patient picks a treatment, fetch matching consent PDF (if any).
  const [consentForTreatment, setConsentForTreatment] = useState(null);
  useEffect(() => {
    setConsentForTreatment(null);
    const target = bookTargetDoctor || me?.doctor_id;
    if (!target || !bookTreatment) return;
    api.get(`/patient-portal/consent-template?treatment=${encodeURIComponent(bookTreatment)}&doctor_id=${target}`, { headers })
      .then((r) => setConsentForTreatment(r.data))
      .catch(() => setConsentForTreatment(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookTreatment, bookTargetDoctor, me?.doctor_id]);

  // ── In-app notifications for new doctor messages ────────────────────
  // Poll unread count for the "Messaggi" tab badge, and listen for Web-Push
  // events from the Service Worker so an open app also shows a toast + badge
  // even when the OS-level system notification is not shown.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    const fetchUnread = () => {
      api.get("/patient-portal/messages/unread-count", { headers })
        .then((r) => { if (!cancelled) setUnreadMsgs(r.data?.unread || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);

    const onSwMessage = (ev) => {
      const d = ev?.data;
      if (!d) return;
      if (d.type === "navigate") {
        try {
          const u = new URL(d.url, window.location.origin);
          const t = u.searchParams.get("tab");
          if (t) setTab(t);
        } catch { /* noop */ }
        return;
      }
      if (d.type !== "push") return;
      const p = d.payload || {};
      // Bump badge immediately (server poll will reconcile)
      setUnreadMsgs((n) => n + 1);
      // If patient is currently on the messages tab, silently refresh — otherwise show toast.
      if (tab === "messages") {
        fetchUnread();
      } else {
        toast(p.title || tr("Nuovo messaggio"), {
          description: p.body || "",
          action: { label: tr("Apri"), onClick: () => setTab("messages") },
        });
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", onSwMessage);
    }
    return () => {
      cancelled = true;
      clearInterval(t);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", onSwMessage);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id, tab]);

  // Clear badge as soon as the patient opens the Messaggi tab (the GET marks-as-read on the server).
  useEffect(() => {
    if (tab !== "messages") return;
    setUnreadMsgs(0);
    api.get("/patient-portal/messages/unread-count", { headers })
      .then((r) => setUnreadMsgs(r.data?.unread || 0))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function switchDoctor(doctor_id) {
    if (switching || doctor_id === me?.doctor_id) return;
    setSwitching(true);
    try {
      await api.patch("/patient-portal/active-doctor", { doctor_id }, { headers });
      const { data } = await api.get("/patient-auth/me", { headers });
      setMe(data.patient);
      const rl = await api.get("/patient-portal/doctors", { headers });
      setDoctors(rl.data || []);
      const rq = await api.get("/patient-portal/requests", { headers });
      setRequests(rq.data || []);
      toast.success(tr("Professionista cambiato"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSwitching(false); }
  }

  async function uploadDocument(files) {
    if (!files || !files.length) return;
    const targetId = docTargetDoctor || me?.doctor_id;
    if (!targetId) { toast.error(tr("Selecciona primero un profesional")); return; }
    setUploadingDoc(true);
    let ok = 0, fail = 0;
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/patient-portal/documents?category=${docCategory}&notes=${encodeURIComponent(docNotes)}&doctor_id=${targetId}`,
          fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
        ok++;
      } catch { fail++; }
    }
    toast.success(`${ok} / ${ok + fail} caricato/i`);
    setDocNotes("");
    try {
      const r = await api.get("/patient-portal/documents", { headers });
      setDocuments(r.data || []);
    } catch { /* ignore */ }
    setUploadingDoc(false);
  }

  async function deleteMyDocument(id) {
    if (!window.confirm(tr("Eliminare questo documento?"))) return;
    try {
      await api.delete(`/patient-portal/documents/${id}`, { headers });
      toast.success(tr("Documento eliminato"));
      const r = await api.get("/patient-portal/documents", { headers });
      setDocuments(r.data || []);
    } catch (e) { toast.error(apiError(e)); }
  }

  async function loadSlots() {
    const target = bookTargetDoctor || me?.doctor_id;
    if (!target || !bookDate) { setBookSlots([]); return; }
    setBookSlotsLoading(true);
    setSelectedSlot("");
    try {
      const dur = getTreatmentDuration(bookTreatment);
      const { data } = await api.get(`/patient-portal/slots?date=${bookDate}&doctor_id=${target}${dur ? `&duration_min=${dur}` : ""}`, { headers });
      setBookSlots(data.slots || []);
    } catch { setBookSlots([]); }
    finally { setBookSlotsLoading(false); }
  }

  async function refreshAppointments() {
    try {
      const r = await api.get("/patient-portal/appointments", { headers });
      setAppointments(r.data || []);
    } catch { /* ignore */ }
  }

  async function bookAppointment() {
    const target = bookTargetDoctor || me?.doctor_id;
    if (!target) { toast.error(tr("Seleziona un professionista")); return; }
    if (!bookDate || !selectedSlot) { toast.error(tr("Seleziona data e orario")); return; }
    setBookConfirming(true);
    try {
      await api.post("/patient-portal/book", {
        doctor_id: target, date: bookDate, time: selectedSlot,
        treatment: bookMode === "consulto" ? `Consulto — ${bookTreatment}` : bookTreatment,
        notes: bookNotes, mode: "prenotazione",
      }, { headers });
      toast.success(tr("Appuntamento confermato ✓"));
      setSelectedSlot(""); setBookNotes("");
      await refreshAppointments();
      await loadSlots();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBookConfirming(false); }
  }

  async function cancelAppointment(aid) {
    if (!window.confirm(tr("Annullare questo appuntamento?"))) return;
    try {
      await api.patch(`/patient-portal/appointments/${aid}`, { action: "cancel" }, { headers });
      toast.success(tr("Appuntamento annullato"));
      refreshAppointments();
    } catch (e) { toast.error(apiError(e)); }
  }

  // Reschedule flow
  const [reschedAppt, setReschedAppt] = useState(null);   // the appointment being rescheduled
  const [reschedDate, setReschedDate] = useState("");
  const [reschedSlot, setReschedSlot] = useState("");
  const [reschedSlots, setReschedSlots] = useState([]);
  const [reschedLoading, setReschedLoading] = useState(false);
  const [reschedSaving, setReschedSaving] = useState(false);

  function openReschedule(a) {
    setReschedAppt(a);
    setReschedDate(a.date.slice(0, 10));
    setReschedSlot("");
    setReschedSlots([]);
  }
  useEffect(() => {
    if (!reschedAppt || !reschedDate) return;
    setReschedLoading(true);
    const dur = reschedAppt.duration_min || 30;
    api.get(`/patient-portal/slots?date=${reschedDate}&doctor_id=${reschedAppt.doctor_id}&duration_min=${dur}`, { headers })
      .then((r) => setReschedSlots(r.data?.slots || []))
      .catch(() => setReschedSlots([]))
      .finally(() => setReschedLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reschedAppt, reschedDate]);

  async function submitReschedule() {
    if (!reschedSlot) { toast.error(tr("Seleziona un orario")); return; }
    setReschedSaving(true);
    try {
      await api.patch(`/patient-portal/appointments/${reschedAppt.id}`,
        { action: "reschedule", date: reschedDate, time: reschedSlot }, { headers });
      toast.success("Appuntamento riprogrammato ✓");
      setReschedAppt(null);
      refreshAppointments();
    } catch (e) { toast.error(apiError(e)); }
    finally { setReschedSaving(false); }
  }

  function logout() {
    localStorage.removeItem("patient_token"); localStorage.removeItem("patient_data");
    if (me?.doctor_slug) navigate(`/reservation/${me.doctor_slug}`); else navigate("/portal");
  }

  async function saveProfile(e) {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.put("/patient-portal/profile", profile, { headers });
      setMe(data.patient); toast.success("Dati aggiornati");
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSaving(false); }
  }

  async function createRequest(e) {
    e.preventDefault(); setCreatingReq(true);
    try {
      const { data } = await api.post("/patient-portal/requests", reqForm, { headers });
      toast.success("Richiesta inviata al medico");
      const rlist = await api.get("/patient-portal/requests", { headers });
      setRequests(rlist.data || []);
      setReqForm({ type: "consulta", treatment: "", message: "" });
      setTab("history");
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setCreatingReq(false); }
  }

  async function uploadPhoto(tag, file) {
    if (!file) return;
    setUploadingTag(tag);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/patient-portal/photos?tag=${tag}`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`Foto ${tag} caricata`);
      const rlist = await api.get("/patient-portal/requests", { headers });
      setRequests(rlist.data || []);
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setUploadingTag(null); }
  }

  if (!me) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center text-stone-500">Cargando…</div>;

  const latestRequest = requests[0];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="patient-portal">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-heading text-xl font-light">Ambra</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-stone-400 hidden sm:inline">patient matter</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="text-right text-xs">
              <div className="text-stone-900">{me.full_name}</div>
              <div className="text-stone-400">{me.email}</div>
            </div>
            <LanguageSwitcher />
            <PushToggle tr={tr} />
            <button onClick={logout} data-testid="logout-btn" className="text-stone-500 hover:text-stone-900"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {doctors.length === 0 && (
          <div className="mb-5 bg-gradient-to-br from-[#FFF7EF] to-white border-2 border-[#D4A373]/40 rounded-2xl p-8 text-center" data-testid="empty-doctors-cta">
            <Users className="w-10 h-10 text-[#B07D4F] mx-auto mb-3" strokeWidth={1.25} />
            <h3 className="font-heading text-2xl font-light mb-2">{tr("Nessun professionista collegato")}</h3>
            <p className="text-stone-600 text-sm mb-5 max-w-md mx-auto">{tr("Per prenotare un appuntamento, cerca prima un professionista della salute e collegalo al tuo profilo.")}</p>
            <Link to="/profesionales" data-testid="search-professionals-cta"
              className="inline-flex items-center gap-2 bg-[#D4A373] text-stone-900 px-6 py-3 rounded-full hover:bg-[#B07D4F] hover:text-white transition-all font-medium">{tr("Cerca un professionista →")}</Link>
          </div>
        )}
        {doctors.length > 0 && (
          <div className="mb-5 bg-white border border-stone-200 rounded-2xl p-4" data-testid="doctor-switcher">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.25em] text-stone-500">
                {doctors.length > 1 ? tr("I tuoi professionisti") : tr("Il tuo professionista")}
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {doctors.map((d) => (
                <button key={d.doctor_id} onClick={() => switchDoctor(d.doctor_id)}
                  disabled={switching || d.is_active}
                  data-testid={`doctor-btn-${d.doctor_id}`}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                    d.is_active
                      ? "bg-[#D4A373] border-[#D4A373] text-stone-900 font-medium cursor-default"
                      : "bg-white border-stone-200 text-stone-600 hover:border-[#D4A373]"
                  }`}>
                  {d.is_active && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5" />}
                  {/^Dr\.?\s+/i.test(d.doctor_name || "") ? d.doctor_name : `Dr. ${d.doctor_name || d.doctor_slug}`}
                </button>
              ))}
            </div>
          </div>
        )}
        <PushBanner tr={tr} />
        <div className="flex items-center gap-3 mb-6 bg-white border border-stone-200 rounded-full p-1 overflow-x-auto">
          {[
            { id: "data", label: tr("I miei dati"), icon: UserCircle },
            { id: "book", label: tr("Prenota"), icon: CalendarClock },
            { id: "messages", label: tr("Messaggi"), icon: Send, badge: unreadMsgs },
            { id: "documents", label: tr("Documenti"), icon: FolderOpen },
            { id: "consents", label: tr("Consensi"), icon: Signature },
            { id: "history", label: tr("Storico"), icon: FileText },
          ].map((tt) => (
            <button key={tt.id} onClick={() => setTab(tt.id)} data-testid={`tab-${tt.id}`}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${tab === tt.id ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-900"}`}>
              <tt.icon className="w-3.5 h-3.5" /> {tt.label}
              {tt.badge > 0 && (
                <span data-testid={`tab-${tt.id}-badge`}
                  className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4A373] text-stone-900 text-[10px] font-semibold">
                  {tt.badge > 99 ? "99+" : tt.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "book" && (
          <div className="space-y-6" data-testid="book-tab-body">
            <div className="rounded-2xl border border-stone-200 bg-gradient-to-br from-[#FBF7F1] to-[#F5EFE6] overflow-hidden">
              <div className="px-6 pt-6 pb-5 border-b border-stone-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarClock className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-light">{tr("Prenota un appuntamento")}</h3>
                </div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-4">{tr("Con quale professionista")}</p>
                {doctors.length === 0 ? (
                  <p className="text-sm text-stone-500 italic">{tr("Non hai ancora professionisti collegati.")}</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {doctors.map((doc) => {
                      const active = (bookTargetDoctor || me?.doctor_id) === doc.doctor_id;
                      const displayName = /^Dr/i.test(doc.doctor_name || "") ? doc.doctor_name : `Dr. ${doc.doctor_name}`;
                      const initials = (doc.doctor_name || "?").replace(/^Dr\.?\s*/i, "").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
                      return (
                        <button key={doc.doctor_id} type="button"
                          onClick={() => setBookTargetDoctor(doc.doctor_id)}
                          data-testid={`book-target-${doc.doctor_id}`}
                          className={`relative flex items-center gap-3.5 pl-3 pr-4 py-3 rounded-xl border transition-all text-left ${active
                            ? "bg-white border-[#D4A373] shadow-[0_4px_16px_-8px_rgba(212,163,115,0.5)] ring-1 ring-[#D4A373]/30"
                            : "bg-white/60 border-stone-200/70 hover:bg-white hover:border-stone-300 hover:shadow-sm"}`}>
                          <span className={`w-11 h-11 rounded-full flex items-center justify-center text-[13px] font-medium tracking-wide flex-shrink-0 transition-all ${active
                            ? "bg-gradient-to-br from-[#D4A373] to-[#B07D4F] text-white shadow-inner"
                            : "bg-stone-100 text-stone-500"}`}>
                            {initials}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-[15px] font-heading font-light text-stone-900 leading-tight truncate">{displayName}</span>
                            <span className={`block text-[10px] uppercase tracking-[0.22em] mt-1 ${active ? "text-[#B07D4F]" : "text-stone-400"}`}>
                              {active ? tr("Selezionato") : tr("Seleziona")}
                            </span>
                          </span>
                          <span className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${active ? "bg-[#D4A373] border-[#D4A373]" : "border-stone-300 bg-white"}`}>
                            {active && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="px-6 py-5 space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2 block">{tr("Che tipo di visita?")}</label>
                  <div className="grid grid-cols-2 gap-2.5">
                    {[
                      { k: "consulto", title: tr("Consulto"), sub: tr("Prima visita per valutare il tuo caso") },
                      { k: "trattamento", title: tr("Trattamento"), sub: tr("Prenota un servizio specifico") },
                    ].map((c) => {
                      const active = bookMode === c.k;
                      return (
                        <button key={c.k} type="button" onClick={() => setBookMode(c.k)}
                          data-testid={`book-mode-${c.k}`}
                          className={`text-left p-4 rounded-xl border transition-all ${active
                            ? "bg-white border-[#D4A373] shadow-[0_4px_16px_-8px_rgba(212,163,115,0.5)] ring-1 ring-[#D4A373]/30"
                            : "bg-white/60 border-stone-200 hover:bg-white hover:border-stone-300"}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${active ? "bg-[#D4A373] border-[#D4A373]" : "border-stone-300"}`} />
                            <span className={`font-heading text-base ${active ? "text-stone-900" : "text-stone-700"}`}>{c.title}</span>
                          </div>
                          <p className="text-xs text-stone-500 mt-1.5 pl-6">{c.sub}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-5">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500">{tr("Scegli il giorno")}</label>
                      <div className="flex items-center gap-1">
                        <button type="button" data-testid="cal-prev"
                          onClick={() => setViewMonth(({ year, month }) => month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 })}
                          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-500">‹</button>
                        <span className="text-xs font-medium text-stone-700 min-w-[110px] text-center capitalize">
                          {new Date(viewMonth.year, viewMonth.month - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
                        </span>
                        <button type="button" data-testid="cal-next"
                          onClick={() => setViewMonth(({ year, month }) => month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 })}
                          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-500">›</button>
                      </div>
                    </div>
                    <MiniCalendar year={viewMonth.year} month={viewMonth.month}
                      days={monthDays} selectedDate={bookDate} minDate={todayIso}
                      onPick={(iso) => setBookDate(iso)} />
                    <div className="flex items-center gap-3 mt-3 text-[10px] uppercase tracking-widest text-stone-500">
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#E8F0E5]" />{tr("disponibile")}</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#F5E4CE]" />{tr("quasi pieno")}</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-stone-200" />{tr("chiuso")}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 block">{bookMode === "consulto" ? "Per quale trattamento vorresti consultare?" : "Trattamento"}</label>
                    {bookTreatments.length > 0 ? (
                      <select value={bookTreatment} onChange={(e) => setBookTreatment(e.target.value)}
                        data-testid="book-treatment"
                        className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#D4A373]">
                        {bookTreatments.map((t) => {
                          const name = typeof t === "string" ? t : t.name;
                          const dur = typeof t === "string" ? null : t.duration_min;
                          return <option key={name} value={name}>{name}{dur ? ` — ${dur} min` : ""}</option>;
                        })}
                      </select>
                    ) : (
                      <input value={bookTreatment} onChange={(e) => setBookTreatment(e.target.value)}
                        data-testid="book-treatment"
                        placeholder={tr("Consulta, controllo...")}
                        className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#D4A373]" />
                    )}
                    <input type="hidden" data-testid="book-date" value={bookDate} readOnly />
                    {consentForTreatment && (
                      <div className="mt-3 rounded-lg bg-[#F5EFE6] border border-[#D4A373]/40 p-3" data-testid="consent-hint">
                        <div className="flex items-start gap-2">
                          <Signature className="w-4 h-4 text-[#B07D4F] mt-0.5" strokeWidth={1.5} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-[#B07D4F] font-medium">{tr("Consenso allegato a questo trattamento")}</div>
                            <div className="text-xs text-stone-600 mt-0.5">{tr("Scarica e leggi il consenso prima di prenotare")}</div>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${consentForTreatment.download_url}`}
                              target="_blank" rel="noreferrer" data-testid="consent-open-link"
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const r = await api.get(consentForTreatment.download_url, { headers, responseType: "blob" });
                                  const url = URL.createObjectURL(r.data);
                                  window.open(url, "_blank");
                                } catch { toast.error("Errore"); }
                              }}
                              className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-[#B07D4F] hover:text-[#8a5a2b]">
                              <Download className="w-3 h-3" /> {tr("Apri consenso")} · {consentForTreatment.filename}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-3 text-xs text-stone-500">
                      {bookDate && (
                        <div className="rounded-lg bg-white border border-stone-200 p-3">
                          <div className="text-[10px] uppercase tracking-widest text-stone-400">{tr("Data selezionata")}</div>
                          <div className="text-sm font-medium text-stone-800 mt-0.5">
                            {new Date(bookDate).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2 block">{tr("Orari disponibili")}</label>
                  {bookSlotsLoading ? (
                    <p className="text-sm text-stone-500 italic">{tr("Cercando disponibilità…")}</p>
                  ) : bookSlots.length === 0 ? (
                    <div className="border-2 border-dashed border-stone-200 rounded-xl p-6 text-center bg-white/60">
                      <Clock className="w-6 h-6 mx-auto mb-2 text-stone-300" strokeWidth={1.4} />
                      <p className="text-sm text-stone-500">{tr("Nessun orario disponibile in questa data.")}</p>
                      <p className="text-xs text-stone-400 mt-1">{tr("Prova un altro giorno o cambia professionista.")}</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2" data-testid="book-slots">
                        {bookSlots.map((s) => {
                          const active = selectedSlot === s;
                          // Highlight adjacent slots that fall inside the treatment duration span.
                          const treatDur = getTreatmentDuration(bookTreatment) || 30;
                          const inSpan = (() => {
                            if (!selectedSlot) return false;
                            const [sh, sm] = s.split(":").map(Number);
                            const [rh, rm] = selectedSlot.split(":").map(Number);
                            const slotStart = sh * 60 + sm;
                            const spanStart = rh * 60 + rm;
                            return !active && slotStart > spanStart && slotStart < spanStart + treatDur;
                          })();
                          return (
                            <button key={s} type="button" onClick={() => setSelectedSlot(s)}
                              data-testid={`slot-${s}`}
                              className={`px-3 py-2 rounded-lg text-sm tabular-nums transition-all ${active
                                ? "bg-gradient-to-br from-[#D4A373] to-[#B07D4F] text-white shadow-sm"
                                : inSpan
                                ? "bg-[#F5E4CE] border border-[#D4A373]/50 text-[#8a5a2b]"
                                : "bg-white border border-stone-200 text-stone-700 hover:border-[#D4A373]"}`}>
                              {s}
                            </button>
                          );
                        })}
                      </div>
                      {selectedSlot && (() => {
                        const dur = getTreatmentDuration(bookTreatment) || 30;
                        const [sh, sm] = selectedSlot.split(":").map(Number);
                        const start = sh * 60 + sm;
                        const end = start + dur;
                        const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
                        return (
                          <div className="mt-3 flex items-center gap-2 text-xs text-stone-600 bg-white border border-[#D4A373]/40 rounded-lg px-3 py-2" data-testid="span-preview">
                            <Clock className="w-3.5 h-3.5 text-[#B07D4F]" strokeWidth={1.6} />
                            <span>La tua sessione occuperà <b>{fmt(start)} – {fmt(end)}</b> ({dur} min)</span>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 block">{tr("Messaggio per il medico (opzionale)")}</label>
                  <textarea value={bookNotes} onChange={(e) => setBookNotes(e.target.value)} rows={3}
                    data-testid="book-notes"
                    placeholder={tr("Racconta cosa vorresti trattare, da quando, aspettative...")}
                    className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#D4A373] resize-y" />
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <button onClick={bookAppointment} disabled={!selectedSlot || bookConfirming}
                    data-testid="book-confirm"
                    className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all ${selectedSlot && !bookConfirming
                      ? "bg-stone-900 text-white hover:bg-stone-700"
                      : "bg-stone-200 text-stone-400 cursor-not-allowed"}`}>
                    <CalendarCheck className="w-4 h-4" />
                    {bookConfirming ? "Confermando..." : (selectedSlot ? `Conferma per ${new Date(bookDate).toLocaleDateString("it-IT", { day: "2-digit", month: "long" })} alle ${selectedSlot}` : "Seleziona un orario")}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <CalendarCheck className="w-4 h-4 text-[#8FA08B]" strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-light">{tr("I tuoi appuntamenti")}</h3>
              </div>
              {appointments.length === 0 ? (
                <p className="text-sm text-stone-500 italic">{tr("Ancora nessun appuntamento.")}</p>
              ) : (
                <ul className="divide-y divide-stone-100" data-testid="my-appointments">
                  {appointments.map((a) => {
                    const dt = new Date(a.date);
                    const past = dt < new Date();
                    const statusStyle = a.status === "confirmed" ? "bg-[#E8F0E5] text-[#4A6E42]"
                      : a.status === "cancelled" ? "bg-stone-100 text-stone-500 line-through"
                      : a.status === "pending" ? "bg-[#FFF4E5] text-[#B07D4F]"
                      : "bg-stone-100 text-stone-600";
                    return (
                      <li key={a.id} data-testid={`appt-${a.id}`}
                        className={`py-4 flex items-center gap-4 ${past ? "opacity-60" : ""}`}>
                        <div className="w-14 flex-shrink-0 text-center">
                          <div className="text-xs uppercase text-stone-400 tracking-widest">{dt.toLocaleString("it-IT", { month: "short" })}</div>
                          <div className="text-2xl font-heading font-light text-stone-900">{dt.getDate()}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-stone-900 truncate">{a.treatment || "Consulta"}</div>
                          <div className="text-xs text-stone-500 mt-0.5">
                            {dt.toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {a.doctor_name || ""}
                            {a.notes && <span className="italic"> · {a.notes}</span>}
                          </div>
                        </div>
                        <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${statusStyle}`}>{a.status}</span>
                        {!past && a.status !== "cancelled" && (
                          <>
                            <button onClick={() => openReschedule(a)} data-testid={`appt-reschedule-${a.id}`}
                              className="text-xs text-[#B07D4F] hover:text-[#8a5a2b] font-medium" title={tr("Riprogramma")}>{tr("Riprogramma")}</button>
                            <button onClick={() => cancelAppointment(a.id)} data-testid={`appt-cancel-${a.id}`}
                              className="text-stone-400 hover:text-red-500" title={tr("Annulla")}>
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}


        {tab === "documents" && (
          <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="documents-tab-body">
            <div className="flex items-center gap-2 mb-4">
              <FolderOpen className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-light">{tr("I tuoi documenti clinici")}</h3>
            </div>
            <p className="text-sm text-stone-500 mb-6">{tr("Analisi, referti, esami istologici e documenti condivisi con il tuo professionista.")}</p>

            <div className="mb-6 rounded-2xl border border-stone-200 bg-gradient-to-br from-[#FBF7F1] to-[#F5EFE6] overflow-hidden" data-testid="patient-doc-uploader">
              <div className="px-6 pt-6 pb-5 border-b border-stone-200/60">
                <p className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-4">{tr("Invia al professionista")}</p>
                {doctors.length === 0 ? (
                  <p className="text-sm text-stone-500 italic">{tr("Non hai ancora professionisti collegati.")}</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {doctors.map((doc) => {
                      const active = (docTargetDoctor || me?.doctor_id) === doc.doctor_id;
                      const displayName = /^Dr/i.test(doc.doctor_name || "") ? doc.doctor_name : `Dr. ${doc.doctor_name}`;
                      const initials = (doc.doctor_name || "?").replace(/^Dr\.?\s*/i, "").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
                      return (
                        <button key={doc.doctor_id} type="button"
                          onClick={() => setDocTargetDoctor(doc.doctor_id)}
                          data-testid={`doc-target-${doc.doctor_id}`}
                          className={`relative flex items-center gap-3.5 pl-3 pr-4 py-3 rounded-xl border transition-all text-left ${active
                            ? "bg-white border-[#D4A373] shadow-[0_4px_16px_-8px_rgba(212,163,115,0.5)] ring-1 ring-[#D4A373]/30"
                            : "bg-white/60 border-stone-200/70 hover:bg-white hover:border-stone-300 hover:shadow-sm"}`}>
                          <span className={`w-11 h-11 rounded-full flex items-center justify-center text-[13px] font-medium tracking-wide flex-shrink-0 transition-all ${active
                            ? "bg-gradient-to-br from-[#D4A373] to-[#B07D4F] text-white shadow-inner"
                            : "bg-stone-100 text-stone-500"}`}>
                            {initials}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-[15px] font-heading font-light text-stone-900 leading-tight truncate">{displayName}</span>
                            <span className={`block text-[10px] uppercase tracking-[0.22em] mt-1 ${active ? "text-[#B07D4F]" : "text-stone-400"}`}>
                              {active ? tr("Destinatario") : tr("Seleziona")}
                            </span>
                          </span>
                          <span className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${active ? "bg-[#D4A373] border-[#D4A373]" : "border-stone-300 bg-white"}`}>
                            {active && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="px-6 py-5">
                <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 block">{tr("Tipo")}</label>
                    <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)} data-testid="patient-doc-category"
                      className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#D4A373]">
                      <option value="analisi">{tr("Analisi")}</option>
                      <option value="istologia">{tr("Istologia")}</option>
                      <option value="ecografia">{tr("Ecografia")}</option>
                      <option value="referto">{tr("Referto")}</option>
                      <option value="preop">{tr("Pre-operatorio")}</option>
                      <option value="altro">{tr("Altro")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 block">{tr("Note (opzionale)")}</label>
                    <input value={docNotes} onChange={(e) => setDocNotes(e.target.value)} data-testid="patient-doc-notes"
                      placeholder={tr("Es. Analisi del sangue del 12/07...")}
                      className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#D4A373]" />
                  </div>
                </div>

                <label className={`mt-4 flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-dashed transition-all cursor-pointer group ${uploadingDoc ? "opacity-60 pointer-events-none border-stone-300" : "border-stone-300 hover:border-[#D4A373] hover:bg-white/70"}`}
                  data-testid="patient-doc-upload-btn">
                  <div className="w-11 h-11 rounded-full bg-[#D4A373]/15 group-hover:bg-[#D4A373]/25 flex items-center justify-center transition-colors flex-shrink-0">
                    <Upload className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-800">
                        {uploadingDoc ? tr("Caricamento in corso…") : tr("Trascina qui il file o fai clic per selezionare")}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">{tr("PDF o immagini · fino a 20 MB · più file consentiti")}</div>
                  </div>
                  <input type="file" accept="application/pdf,image/*" multiple hidden
                    onChange={(e) => { uploadDocument(Array.from(e.target.files || [])); e.target.value = ""; }} />
                </label>
                <p className="text-[11px] text-stone-500 mt-3 flex items-center gap-1.5">
                  <Info className="w-3 h-3 text-stone-400" strokeWidth={1.5} />{tr("Solo il professionista selezionato potrà vedere questo documento.")}</p>
              </div>
            </div>

            {documents.length === 0 ? (
              <div className="border border-dashed border-stone-200 rounded-xl p-10 text-center">
                <FolderOpen className="w-10 h-10 mx-auto mb-3 text-stone-300" strokeWidth={1.25} />
                <p className="text-stone-500 text-sm">{tr("Non ci sono ancora documenti. Carica il primo qui sopra oppure aspetta che il tuo medico condivida qualcosa con te.")}</p>
              </div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {documents.map((d) => (
                  <li key={d.id} data-testid={`patient-doc-${d.id}`} className="py-4 flex items-center gap-4">
                    <FileText className="w-5 h-5 text-[#D4A373] flex-shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-900 truncate">{d.original_filename}</div>
                      <div className="text-xs text-stone-500 flex items-center gap-2 flex-wrap mt-1">
                        <span className="px-2 py-0.5 rounded-full bg-[#FAF9F6] border border-stone-200 uppercase text-[10px] tracking-widest">{d.category || "altro"}</span>
                        {d.uploaded_by === "patient" ? (
                          <span data-testid={`patient-doc-mine-${d.id}`}
                            className="px-2 py-0.5 rounded-full bg-[#D4A373]/25 text-[#8a5a2b] uppercase text-[10px] tracking-widest">{tr("Caricato da te")}</span>
                        ) : (
                          d.doctor_name && <span>· {/^Dr/i.test(d.doctor_name) ? d.doctor_name : `Dr. ${d.doctor_name}`}</span>
                        )}
                        {d.created_at && <span>· {new Date(d.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</span>}
                      </div>
                      {d.notes && <p className="text-xs text-stone-500 italic mt-1">{d.notes}</p>}
                    </div>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/patient-portal/documents/${d.id}/download`}
                      target="_blank" rel="noreferrer" data-testid={`patient-doc-open-${d.id}`}
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const res = await api.get(`/patient-portal/documents/${d.id}/download`, { headers, responseType: "blob" });
                          const url = URL.createObjectURL(res.data);
                          window.open(url, "_blank");
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch (err) { toast.error(apiError(err)); }
                      }}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stone-200 hover:bg-stone-50 text-stone-700">
                      <ExternalLink className="w-3 h-3" />{tr("Apri")}</a>
                    <button onClick={async () => {
                      try {
                        const res = await api.get(`/patient-portal/documents/${d.id}/download`, { headers, responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url; a.download = d.original_filename || "documento";
                        document.body.appendChild(a); a.click(); a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (err) { toast.error(apiError(err)); }
                    }} data-testid={`patient-doc-download-${d.id}`}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white">
                      <Download className="w-3 h-3" />{tr("Scarica")}</button>
                    {d.uploaded_by === "patient" && (
                      <button onClick={() => deleteMyDocument(d.id)} data-testid={`patient-doc-del-${d.id}`}
                        className="text-stone-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "consents" && (
          <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="consents-tab-body">
            <div className="flex items-center gap-2 mb-4">
              <Signature className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-light">{tr("I consensi firmati")}</h3>
            </div>
            <p className="text-sm text-stone-500 mb-6">{tr("Consensi informati che hai firmato digitalmente.")}</p>
            {consents.length === 0 ? (
              <div className="border border-dashed border-stone-200 rounded-xl p-10 text-center">
                <Signature className="w-10 h-10 mx-auto mb-3 text-stone-300" strokeWidth={1.25} />
                <p className="text-stone-500 text-sm">{tr("Non hai ancora firmato consensi.")}</p>
              </div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {consents.map((c) => (
                  <li key={c.id} data-testid={`patient-consent-${c.id}`} className="py-4 flex items-center gap-4">
                    <Signature className="w-5 h-5 text-[#D4A373] flex-shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-900 truncate">{c.template_name || "Consenso informato"}</div>
                      <div className="text-xs text-stone-500 mt-1">
                        {c.treatment && <span>{c.treatment} · </span>}
                        {c.doctor_name && <span>{/^Dr/i.test(c.doctor_name) ? c.doctor_name : `Dr. ${c.doctor_name}`} · </span>}
                        {c.signed_at && <span>Firmato il {new Date(c.signed_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</span>}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-[#5F7059] bg-[#8FA08B]/15 px-2 py-1 rounded-full">{tr("Firmato")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "data" && (
          <div className="space-y-6">
            <InstallAppButton />
            <form onSubmit={saveProfile} className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4" data-testid="profile-form">
            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["full_name", "Nome e cognome", "text"],
                ["phone", "Telefono", "tel"],
                ["date_of_birth", "Data di nascita", "date"],
                ["codice_fiscale", "Codice fiscale", "text"],
              ].map(([k, l, t2]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1.5 block">{tr(l)}</label>
                  <input type={t2} value={profile[k]} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} data-testid={`profile-${k}`}
                    className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B]" />
                </div>
              ))}
            </div>
            {[
              ["allergies", "Allergie", "Es. lattice, penicillina…"],
              ["diseases", "Patologie", "Es. ipertensione, diabete…"],
              ["medications", "Farmaci in uso", "Elenca i farmaci che assumi"],
              ["notes", "Note aggiuntive", "Qualsiasi informazione rilevante"],
            ].map(([k, l, ph]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1.5 block">{tr(l)}</label>
                <textarea value={profile[k]} placeholder={tr(ph)} rows={2} spellCheck onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} data-testid={`profile-${k}`}
                  className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B] resize-y" />
              </div>
            ))}
            <button type="submit" disabled={saving} data-testid="save-profile"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
              <Save className="w-4 h-4" /> {saving ? "Salvataggio…" : "Salva dati"}
            </button>
          </form>
          </div>
        )}

        {tab === "request" && (
          <form onSubmit={createRequest} className="bg-white border border-stone-200 rounded-2xl p-6 space-y-5" data-testid="request-form">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-stone-400 mb-2">{tr("Nuova richiesta")}</p>
              <h2 className="font-heading text-2xl font-light">{tr("Cosa vorresti prenotare?")}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <label className={`p-4 rounded-2xl border cursor-pointer ${reqForm.type === "consulta" ? "border-[#D4A373] bg-[#D4A373]/10" : "border-stone-200"}`}>
                <input type="radio" name="type" value="consulta" checked={reqForm.type === "consulta"} onChange={(e) => setReqForm({ ...reqForm, type: e.target.value })} className="hidden" data-testid="type-consulta" />
                <div className="font-medium">{tr("Consulto")}</div>
                <div className="text-xs text-stone-500 mt-1">{tr("Prima visita per valutare la tua richiesta.")}</div>
              </label>
              <label className={`p-4 rounded-2xl border cursor-pointer ${reqForm.type === "tratamento" ? "border-[#D4A373] bg-[#D4A373]/10" : "border-stone-200"}`}>
                <input type="radio" name="type" value="tratamento" checked={reqForm.type === "tratamento"} onChange={(e) => setReqForm({ ...reqForm, type: e.target.value })} className="hidden" data-testid="type-tratamento" />
                <div className="font-medium">{tr("Trattamento")}</div>
                <div className="text-xs text-stone-500 mt-1">{tr("Prenota un trattamento specifico.")}</div>
              </label>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1.5 block">{tr("Trattamento di interesse")}</label>
              <select value={reqForm.treatment} onChange={(e) => setReqForm({ ...reqForm, treatment: e.target.value })} data-testid="request-treatment"
                className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B]">
                <option value="">{tr("-- Seleziona --")}</option>
                {TREATMENTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1.5 block">{tr("Messaggio per il medico")}</label>
              <textarea value={reqForm.message} rows={3} spellCheck placeholder={tr("Racconta cosa vorresti trattare, da quando, aspettative…")} data-testid="request-message"
                onChange={(e) => setReqForm({ ...reqForm, message: e.target.value })}
                className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B] resize-y" />
            </div>
            <button type="submit" disabled={creatingReq} data-testid="submit-request"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors disabled:opacity-50 font-medium">
              <Send className="w-4 h-4" /> {creatingReq ? "…" : "Invia richiesta"}
            </button>
          </form>
        )}

        {tab === "messages" && <MessagesTab tr={tr} doctors={doctors} activeDoctorId={me?.doctor_id} activeDoctorName={me?.doctor_name} />}

        {tab === "history" && (
          <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-3" data-testid="history-section">
            <h3 className="font-heading text-xl font-light mb-2">{tr("Le tue richieste")}</h3>
            {requests.length === 0 && <p className="text-sm text-stone-500">{tr("Ancora nessuna richiesta.")}</p>}
            {requests.map((r) => (
              <div key={r.id} className="flex items-center gap-4 p-4 rounded-xl bg-[#FAF9F6] border border-stone-100" data-testid={`req-${r.id}`}>
                <Clock className="w-4 h-4 text-stone-400" />
                <div className="flex-1">
                  <div className="text-sm text-stone-900 font-medium">{r.type === "consulta" ? "Consulto" : "Trattamento"} · {r.treatment || "—"}</div>
                  <div className="text-xs text-stone-500">{new Date(r.created_at).toLocaleString("it-IT")} · {r.photos?.length || 0} foto</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${r.status === "approved" ? "bg-[#E8F0E5] text-[#4A6E42]" : r.status === "rejected" ? "bg-[#FBE9E7] text-[#B4453E]" : "bg-stone-100 text-stone-600"}`}>
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reschedule modal */}
      {reschedAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4"
          onClick={() => setReschedAppt(null)} data-testid="reschedule-modal">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <div>
                <div className="font-heading text-xl font-light">{tr("Riprogramma appuntamento")}</div>
                <div className="text-xs text-stone-500 mt-0.5">{reschedAppt.treatment} · {new Date(reschedAppt.date).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <button onClick={() => setReschedAppt(null)} className="text-stone-400 hover:text-stone-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 block">{tr("Nuova data")}</label>
                <input type="date" value={reschedDate} min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setReschedDate(e.target.value)}
                  data-testid="reschedule-date"
                  className="w-full px-3 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#D4A373]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2 block">{tr("Nuovo orario")}</label>
                {reschedLoading ? (
                  <p className="text-sm text-stone-500 italic">{tr("Cercando disponibilità…")}</p>
                ) : reschedSlots.length === 0 ? (
                  <p className="text-sm text-stone-500 italic">{tr("Nessun orario disponibile in questa data.")}</p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="reschedule-slots">
                    {reschedSlots.map((s) => (
                      <button key={s} type="button" onClick={() => setReschedSlot(s)}
                        data-testid={`reschedule-slot-${s}`}
                        className={`px-2 py-2 rounded-lg text-sm tabular-nums transition-all ${reschedSlot === s
                          ? "bg-gradient-to-br from-[#D4A373] to-[#B07D4F] text-white shadow-sm"
                          : "bg-white border border-stone-200 text-stone-700 hover:border-[#D4A373]"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setReschedAppt(null)}
                  className="px-4 py-2 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50 text-sm">{tr("Annulla")}</button>
                <button onClick={submitReschedule} disabled={!reschedSlot || reschedSaving}
                  data-testid="reschedule-confirm"
                  className={`px-5 py-2 rounded-full text-sm font-medium ${reschedSlot && !reschedSaving ? "bg-stone-900 text-white hover:bg-stone-700" : "bg-stone-200 text-stone-400 cursor-not-allowed"}`}>
                  {reschedSaving ? "Salvando..." : "Conferma nuovo orario"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Mini month calendar with per-day availability tint (used in the Prenota tab).
function MiniCalendar({ year, month, days, selectedDate, minDate, onPick }) {
  // Build a 6-week grid Mon-Sun
  const firstDay = new Date(year, month - 1, 1);
  const wd = (firstDay.getDay() + 6) % 7; // Mon=0
  const last = new Date(year, month, 0).getDate();
  const dayMap = {};
  (days || []).forEach((d) => { dayMap[d.date] = d; });
  const cells = [];
  for (let i = 0; i < wd; i++) cells.push(null);
  for (let d = 1; d <= last; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const labels = ["L", "M", "M", "G", "V", "S", "D"];
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-3" data-testid="mini-calendar">
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-stone-400 mb-1 text-center">
        {labels.map((l, i) => <div key={i}>{l}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (c === null) return <div key={i} className="h-9" />;
          const iso = `${year}-${String(month).padStart(2, "0")}-${String(c).padStart(2, "0")}`;
          const info = dayMap[iso];
          const isPast = minDate && iso < minDate;
          const isSel = iso === selectedDate;
          let color = "bg-stone-200 text-stone-400";  // default: no data / closed
          let disabled = isPast;
          if (info) {
            if (info.blocked) { color = "bg-red-100 text-red-500 line-through"; disabled = true; }
            else if (info.disabled) { color = "bg-stone-100 text-stone-400"; disabled = true; }
            else if (info.free_count === 0) { color = "bg-stone-100 text-stone-400 line-through"; disabled = true; }
            else if (info.free_count <= 3) { color = "bg-[#F5E4CE] text-[#8a5a2b] hover:bg-[#E8CDA1]"; }
            else { color = "bg-[#E8F0E5] text-[#4A6E42] hover:bg-[#D4E4CE]"; }
          }
          if (isPast) color = "bg-stone-50 text-stone-300";
          return (
            <button key={i} type="button" disabled={disabled}
              onClick={() => !disabled && onPick(iso)}
              data-testid={`cal-day-${iso}`}
              className={`h-9 rounded-md text-sm tabular-nums transition-all relative ${color} ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${isSel ? "ring-2 ring-[#D4A373] font-semibold" : ""}`}>
              {c}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MessagesTab({ tr, doctors, activeDoctorId, activeDoctorName }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem("patient_token")}` };

  async function load() {
    try {
      const { data } = await api.get("/patient-portal/messages", { headers });
      setItems(data || []);
      setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 50);
    } catch { /* ignore */ }
  }
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [activeDoctorId]);

  async function send(e) {
    e.preventDefault();
    if (!text.trim() || sending) return;
    if (!activeDoctorId) { toast.error(tr("Seleziona prima un professionista")); return; }
    setSending(true);
    try {
      await api.post("/patient-portal/messages", { message: text.trim() }, { headers });
      setText("");
      await load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  }

  const docName = activeDoctorName || doctors.find((d) => d.doctor_id === activeDoctorId)?.name || tr("il tuo medico");
  return (
    <div className="space-y-4" data-testid="messages-section">
      <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6">
        <div className="flex items-center gap-2 mb-1">
          <Send className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
          <h3 className="font-heading text-xl font-light">{tr("Messaggi")}</h3>
        </div>
        <p className="text-sm text-stone-500">{tr("Conversazione con")} <span className="text-stone-800 font-medium">{docName}</span></p>
      </div>

      <div ref={listRef} data-testid="messages-list"
        className="bg-white border border-stone-200 rounded-2xl p-4 md:p-5 max-h-[420px] min-h-[240px] overflow-y-auto space-y-3">
        {items.length === 0 ? (
          <p className="text-sm text-stone-400 italic text-center py-8">{tr("Ancora nessun messaggio. Scrivi il primo qui sotto.")}</p>
        ) : (
          items.map((m) => (
            <div key={m.id} data-testid={`msg-${m.id}`}
              className={`flex ${m.direction === "in" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                m.direction === "in" ? "bg-[#D4A373] text-stone-900" : "bg-[#FAF9F6] border border-stone-200 text-stone-800"
              }`}>
                <div className="whitespace-pre-wrap break-words">{m.message}</div>
                <div className={`text-[10px] mt-1 ${m.direction === "in" ? "text-stone-800/60" : "text-stone-400"}`}>
                  {new Date(m.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <form onSubmit={send} className="flex items-end gap-2 bg-white border border-stone-200 rounded-2xl p-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder={tr("Scrivi un messaggio…")} data-testid="msg-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
          className="flex-1 resize-none bg-transparent px-3 py-2 focus:outline-none text-sm" />
        <button type="submit" disabled={sending || !text.trim()} data-testid="msg-send"
          className="flex items-center gap-1 px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-700 disabled:opacity-40">
          <Send className="w-3.5 h-3.5" /> {sending ? tr("Invio…") : tr("Invia")}
        </button>
      </form>
    </div>
  );
}

function PushBanner({ tr }) {
  const [state, setState] = useState("loading");
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("push_banner_dismissed") === "1"; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);

  const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

  useEffect(() => {
    if (!isPushSupported()) { setState("unsupported"); return; }
    (async () => {
      try {
        await ensureServiceWorker();
        const perm = await currentPushPermission();
        if (perm === "denied") { setState("denied"); return; }
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setState(sub && perm === "granted" ? "on" : "off");
      } catch { setState("off"); }
    })();
  }, []);

  async function enable() {
    setBusy(true);
    try {
      await subscribeToPush();
      setState("on");
      toast.success(tr("Notifiche attivate"));
    } catch (e) {
      const msg = String(e?.message || e);
      toast.error(msg);
      if (msg.toLowerCase().includes("negato") || msg.toLowerCase().includes("denied")) setState("denied");
    } finally { setBusy(false); }
  }

  function dismiss() {
    try { localStorage.setItem("push_banner_dismissed", "1"); } catch { /* noop */ }
    setDismissed(true);
  }

  if (state === "loading" || state === "on" || state === "unsupported" || dismissed) return null;

  // iOS Safari requires the app to be added to Home Screen before Web Push works.
  if (isIos && !isStandalone) {
    return (
      <div data-testid="push-banner-ios"
        className="mb-5 bg-gradient-to-br from-[#FFF7EF] to-white border border-[#D4A373]/40 rounded-2xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#D4A373]/20 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-[#B07D4F]" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-lg font-light mb-1">{tr("Attiva le notifiche del medico")}</h4>
          <p className="text-sm text-stone-600 mb-2">{tr("Per ricevere gli avvisi sull'iPhone devi prima aggiungere Ambra alla schermata Home:")}</p>
          <ol className="text-sm text-stone-600 list-decimal ml-5 space-y-0.5">
            <li>{tr("Tocca il pulsante Condividi in Safari")}</li>
            <li>{tr("Scegli \"Aggiungi a Home\"")}</li>
            <li>{tr("Apri Ambra dall'icona sulla Home e attiva le notifiche")}</li>
          </ol>
        </div>
        <button onClick={dismiss} data-testid="push-banner-dismiss"
          className="text-stone-400 hover:text-stone-900 flex-shrink-0" title={tr("Chiudi")}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div data-testid="push-banner-denied"
        className="mb-5 bg-gradient-to-br from-[#FFF7EF] to-white border border-[#D4A373]/40 rounded-2xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#D4A373]/20 flex items-center justify-center flex-shrink-0">
          <BellOff className="w-5 h-5 text-[#B07D4F]" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-lg font-light mb-1">{tr("Notifiche bloccate")}</h4>
          <p className="text-sm text-stone-600">{tr("Hai negato le notifiche nel browser. Aprile dalle impostazioni del sito per ricevere i messaggi del medico.")}</p>
        </div>
        <button onClick={dismiss} data-testid="push-banner-dismiss"
          className="text-stone-400 hover:text-stone-900 flex-shrink-0" title={tr("Chiudi")}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="push-banner"
      className="mb-5 bg-gradient-to-br from-[#FFF7EF] to-white border border-[#D4A373]/40 rounded-2xl p-5 flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-[#D4A373]/20 flex items-center justify-center flex-shrink-0">
        <Bell className="w-5 h-5 text-[#B07D4F]" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-heading text-lg font-light mb-1">{tr("Attiva le notifiche del medico")}</h4>
        <p className="text-sm text-stone-600 mb-3">{tr("Ricevi un avviso istantaneo sul telefono quando il tuo professionista ti scrive o cambia un appuntamento.")}</p>
        <button onClick={enable} disabled={busy} data-testid="push-banner-enable"
          className="inline-flex items-center gap-2 bg-[#D4A373] text-stone-900 px-5 py-2 rounded-full text-sm font-medium hover:bg-[#B07D4F] hover:text-white transition-colors disabled:opacity-50">
          <Bell className="w-3.5 h-3.5" /> {busy ? tr("Attivando…") : tr("Attiva ora")}
        </button>
      </div>
      <button onClick={dismiss} data-testid="push-banner-dismiss"
        className="text-stone-400 hover:text-stone-900 flex-shrink-0" title={tr("Chiudi")}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}


function PushToggle({ tr }) {
  const [state, setState] = useState("loading"); // loading | on | off | denied | unsupported
  useEffect(() => {
    if (!isPushSupported()) { setState("unsupported"); return; }
    (async () => {
      try {
        await ensureServiceWorker();
        const perm = await currentPushPermission();
        if (perm === "denied") { setState("denied"); return; }
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setState(sub && perm === "granted" ? "on" : "off");
      } catch { setState("off"); }
    })();
  }, []);
  async function toggle() {
    if (state === "on") {
      try { await unsubscribeFromPush(); setState("off"); toast.success(tr("Notifiche disattivate")); }
      catch (e) { toast.error(String(e.message || e)); }
    } else {
      try { await subscribeToPush(); setState("on"); toast.success(tr("Notifiche attivate")); }
      catch (e) { toast.error(String(e.message || e)); if (String(e.message || "").includes("negato")) setState("denied"); }
    }
  }
  if (state === "unsupported") return null;
  if (state === "denied") return (
    <span title={tr("Notifiche bloccate nelle impostazioni del browser")} className="text-stone-300" data-testid="push-blocked">
      <BellOff className="w-4 h-4" />
    </span>
  );
  const on = state === "on";
  return (
    <button onClick={toggle} disabled={state === "loading"} data-testid="push-toggle"
      title={on ? tr("Disattiva notifiche") : tr("Attiva notifiche push sul telefono")}
      className={`p-1 transition-colors ${on ? "text-[#B07D4F] hover:text-[#8a5a2b]" : "text-stone-400 hover:text-stone-900"}`}>
      {on ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
    </button>
  );
}

